import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchStructureTemplates, saveSystemTemplate } from "../api/projectStructureApi";
import { CATEGORIES, LOCATION_RULES } from "../structure/structureRules";
import { TemplateEditError, addRow, cleanPresets, findRow, moveRow, newTemplateId, removeRow, updateRow } from "../structure/templateOps";
import { confirmDialog } from "../utils/confirmDialog";

const TREES = [
  { key: "tree", label: "შენობა (მთელი შაბლონი)" },
  { key: "parking_tree", label: "მიწისქვეშა პარკინგი" },
];
const row = { display: "flex", alignItems: "center", gap: 6, padding: "3px 4px", borderBottom: "1px solid rgba(148,163,184,0.15)", cursor: "pointer" };
const muted = { opacity: 0.6, fontSize: 11 };
const tag = { fontSize: 10.5, borderRadius: 8, padding: "0 6px", border: "1px solid rgba(148,163,184,0.4)" };
const field = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };

/**
 * The default structure template (library and presets) every new project starts from:
 * disciplines and systems of a building and of the shared parking, and which top-level rows
 * the common object (0) and each building get when the plot has several buildings.
 */
export default function StructureLibraryPanel({ token, isSuperuser }) {
  const [system, setSystem] = useState(undefined);
  const [draft, setDraft] = useState(null); // { tree, parking_tree, presets }
  const [treeKey, setTreeKey] = useState("tree");
  const [sel, setSel] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await fetchStructureTemplates(token);
    setSystem(data.system);
    setDraft(data.system ? { tree: data.system.tree || [], parking_tree: data.system.parking_tree || [], presets: data.system.presets || {} } : null);
  }, [token]);

  useEffect(() => {
    load().catch((err) => setMessage({ kind: "error", text: err.message }));
  }, [load]);

  const dirty = useMemo(
    () => !!system && !!draft && JSON.stringify({ t: draft.tree, p: draft.parking_tree, s: draft.presets }) !== JSON.stringify({ t: system.tree, p: system.parking_tree, s: system.presets }),
    [system, draft]
  );
  const rows = draft?.[treeKey] || [];
  const hit = sel ? findRow(rows, sel) : null;
  const edit = (fn) => {
    try {
      setDraft((d) => ({ ...d, [treeKey]: fn(d[treeKey]) }));
      setMessage(null);
    } catch (err) {
      if (err instanceof TemplateEditError) setMessage({ kind: "error", text: err.message });
      else throw err;
    }
  };
  const add = (parentId, kind) => {
    const r = { id: newTemplateId(), kind, name_ka: kind === "system" ? "ახალი სისტემა" : "ახალი დისციპლინა" };
    try {
      const next = addRow(rows, parentId, r);
      setDraft((d) => ({ ...d, [treeKey]: next }));
      setSel(r.id);
      setMessage(null);
    } catch (err) {
      if (err instanceof TemplateEditError) setMessage({ kind: "error", text: err.message });
      else throw err;
    }
  };
  const remove = async (r) => {
    const n = (x) => 1 + (x.children || []).reduce((s, c) => s + n(c), 0);
    const count = n(r) - 1;
    if (!(await confirmDialog(`წაიშალოს „${r.name_ka}“${count ? ` და მისი ${count} ქვე-ხაზი` : ""}? უკვე შექმნილ პროექტებს არ შეეხება.`, { confirmLabel: "წაშლა", cancelLabel: "გაუქმება" }))) return;
    edit((t) => removeRow(t, r.id));
    setSel(null);
  };
  const togglePreset = (key, id, on) =>
    setDraft((d) => {
      const list = new Set(d.presets[key] || []);
      if (on) list.add(id);
      else list.delete(id);
      return { ...d, presets: { ...d.presets, [key]: [...list] } };
    });
  const save = async () => {
    const unnamed = [...draft.tree, ...draft.parking_tree].flatMap(function walk(x) {
      return [...(String(x.name_ka || "").trim() ? [] : [x.code]), ...(x.children || []).flatMap(walk)];
    });
    if (unnamed.length) return setMessage({ kind: "error", text: `დასახელება აკლია: ${unnamed.join(", ")}` });
    setBusy(true);
    try {
      const saved = await saveSystemTemplate(token, {
        tree: draft.tree,
        parking_tree: draft.parking_tree,
        presets: cleanPresets(draft.presets, [...draft.tree, ...draft.parking_tree]),
      });
      setSystem(saved);
      setDraft({ tree: saved.tree || [], parking_tree: saved.parking_tree || [], presets: saved.presets || {} });
      setMessage({ kind: "ok", text: "შაბლონი შეინახა — ახალი პროექტები ამით დაიწყება." });
    } catch (err) {
      setMessage({ kind: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };
  const createEmpty = async () => {
    setBusy(true);
    try {
      await saveSystemTemplate(token, { name: "ნაგულისხმევი შაბლონი", tree: [{ id: newTemplateId(), code: "1", kind: "discipline", name_ka: "ახალი დისციპლინა", category: "hard" }], parking_tree: [], presets: {} });
      await load();
    } catch (err) {
      setMessage({ kind: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (system === undefined) return <div className="panel">იტვირთება…</div>;
  if (!system) {
    return (
      <div className="panel" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div>ნაგულისხმევი შაბლონი ბაზაში ჯერ არ არის.</div>
        {isSuperuser && (
          <button type="button" className="primary" disabled={busy} onClick={createEmpty}>
            ცარიელი შაბლონის შექმნა
          </button>
        )}
        {message && <div className="msg-error">{message.text}</div>}
      </div>
    );
  }

  const renderRows = (list, depth) =>
    list.map((r) => {
      const children = r.children || [];
      const open = !collapsed.has(r.id);
      return (
        <React.Fragment key={r.id}>
          <div style={{ ...row, paddingLeft: 4 + depth * 16, background: sel === r.id ? "rgba(6,182,212,0.16)" : undefined }} onClick={() => setSel(r.id)}>
            <button
              type="button"
              className="link"
              style={{ width: 18, visibility: children.length ? "visible" : "hidden" }}
              onClick={(e) => {
                e.stopPropagation();
                setCollapsed((s) => {
                  const n = new Set(s);
                  if (n.has(r.id)) n.delete(r.id);
                  else n.add(r.id);
                  return n;
                });
              }}
            >
              {open ? "▾" : "▸"}
            </button>
            <span style={{ ...muted, minWidth: 40 }}>{r.code}</span>
            {r.kind === "system" && <span>▣</span>}
            <span style={{ flex: 1, minWidth: 0 }}>{r.name_ka}</span>
            {r.in_preset === false && <span style={tag}>მხოლოდ ბიბლიოთეკაში</span>}
            {r.sheet_code && <span style={tag}>{r.sheet_code}</span>}
            {depth === 0 && r.category && <span style={muted}>{r.category}</span>}
          </div>
          {open && renderRows(children, depth + 1)}
        </React.Fragment>
      );
    });

  const r = hit?.row;
  const isTop = hit && !hit.parent;
  const set = (patch) => edit((t) => updateRow(t, r.id, patch));

  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12, opacity: 0.8 }}>
        „{system.name}“ — ყველა ახალი პროექტი ამ სტრუქტურით იწყება და ეს არის სტრუქტურის რედაქტორის ბიბლიოთეკა. ცვლილება უკვე შექმნილ პროექტებს არ ეხება.
        {!isSuperuser && " რედაქტირება მხოლოდ ადმინისტრატორს შეუძლია."}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {TREES.map((t) => (
          <button key={t.key} type="button" className={treeKey === t.key ? "primary" : "boq-action-btn"} onClick={() => (setTreeKey(t.key), setSel(null))}>
            {t.label}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {isSuperuser && (
          <>
            <button type="button" className="boq-action-btn" onClick={() => add(null, "discipline")}>
              + დისციპლინა
            </button>
            <button type="button" className="boq-action-btn" disabled={!dirty || busy} onClick={() => load()}>
              გაუქმება
            </button>
            <button type="button" className="primary" disabled={!dirty || busy} onClick={save}>
              {busy ? "ინახება…" : "შენახვა"}
            </button>
          </>
        )}
      </div>
      {message && <div className={message.kind === "ok" ? "msg-success" : "msg-error"}>{message.text}</div>}

      <div style={{ maxHeight: "45vh", overflow: "auto", border: "1px solid rgba(148,163,184,0.3)", borderRadius: 6 }}>{renderRows(rows, 0)}</div>

      {r && (
        <fieldset disabled={!isSuperuser} style={{ border: "1px solid rgba(148,163,184,0.35)", borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
          <div style={field}>
            <b style={{ flex: 1 }}>
              {r.code} {r.kind === "system" ? "▣ სისტემა (Excel-ში ბლოკი)" : isTop ? "დისციპლინა" : "ქვე-დისციპლინა"}
            </b>
            <button type="button" className="boq-action-btn" onClick={() => edit((t) => moveRow(t, r.id, -1))}>
              ↑
            </button>
            <button type="button" className="boq-action-btn" onClick={() => edit((t) => moveRow(t, r.id, 1))}>
              ↓
            </button>
            {r.kind === "discipline" && (
              <>
                <button type="button" className="boq-action-btn" onClick={() => add(r.id, "discipline")}>
                  + ქვე-დისციპლინა
                </button>
                <button type="button" className="boq-action-btn" onClick={() => add(r.id, "system")}>
                  + ▣ სისტემა
                </button>
              </>
            )}
            <button type="button" className="boq-action-btn" onClick={() => remove(r)}>
              🗑
            </button>
          </div>
          <div style={field}>
            <label style={{ minWidth: 90 }}>დასახელება</label>
            <input style={{ flex: 1, minWidth: 160 }} value={r.name_ka || ""} onChange={(e) => set({ name_ka: e.target.value })} />
            <input style={{ flex: 1, minWidth: 160 }} placeholder="English" value={r.name_en || ""} onChange={(e) => set({ name_en: e.target.value })} />
          </div>
          <div style={field}>
            <label style={{ minWidth: 90 }}>ტიპი</label>
            <select value={r.kind} onChange={(e) => set({ kind: e.target.value })}>
              <option value="discipline">დისციპლინა</option>
              <option value="system">▣ სისტემა</option>
            </select>
            {r.kind === "discipline" && (
              <>
                <label>ფურცლის კოდი</label>
                <input style={{ width: 90 }} maxLength={12} value={r.sheet_code || ""} onChange={(e) => set({ sheet_code: e.target.value.toUpperCase() })} />
              </>
            )}
          </div>
          {isTop && (
            <div style={field}>
              <label style={{ minWidth: 90 }}>კატეგორია</label>
              <select value={r.category || ""} onChange={(e) => set({ category: e.target.value })}>
                <option value="">ზედა ხაზისგან</option>
                {Object.entries(CATEGORIES).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          )}
          {r.kind === "discipline" && (
            <div style={field}>
              <label style={{ minWidth: 90 }}>ლოკაცია</label>
              <select value={r.location_rule || ""} onChange={(e) => set({ location_rule: e.target.value })}>
                <option value="">{isTop ? "ობიექტი (ნაგულისხმევი)" : "მემკვიდრეობით"}</option>
                {Object.entries(LOCATION_RULES).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!isTop && (
            <label style={field}>
              <input type="checkbox" checked={r.in_preset !== false} onChange={(e) => set({ in_preset: e.target.checked ? "" : false })} />
              ახალ პროექტში ავტომატურად ემატება (გამორთული — მხოლოდ ბიბლიოთეკაში ჩანს)
            </label>
          )}
          {isTop && treeKey === "tree" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={muted}>როცა ნაკვეთზე რამდენიმე შენობაა (ერთი შენობა იღებს ყველაფერს):</span>
              <label style={field}>
                <input type="checkbox" checked={(draft.presets.building_with_others || []).includes(r.id)} onChange={(e) => togglePreset("building_with_others", r.id, e.target.checked)} />
                ყოველ შენობაში
              </label>
              <label style={field}>
                <input type="checkbox" checked={(draft.presets.common_object_0 || []).includes(r.id)} onChange={(e) => togglePreset("common_object_0", r.id, e.target.checked)} />
                საერთო ობიექტში (0)
              </label>
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
}
